import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Tesseract from 'tesseract.js';

// ── Parseo RENAPER (PDF417 / QR) ──────────────────────────────────────────────
function parseRenaperDni(raw) {
  const partes = raw.split('@').map(p => p.trim());
  console.log('[QrDniReader] Partes raw:', partes);

  const base = partes[0] === '' ? 1 : 0;
  const apellido        = partes[base + 1] || '';
  const nombre          = partes[base + 2] || '';
  const sexo            = partes[base + 3] || '';
  let   dniParte        = partes[base + 4] || '';
  const fechaNacimiento = partes[base + 6] || '';

  if (!/^\d{7,8}$/.test(dniParte)) {
    const candidato = partes.find(p => /^\d{7,8}$/.test(p));
    if (candidato) { console.log('[QrDniReader] DNI fallback:', candidato); dniParte = candidato; }
  }
  return { apellido, nombre, sexo, dni: dniParte, fechaNacimiento };
}

// ── Parseo MRZ (TD1: 3 líneas × 30 chars, formato DNI argentino) ─────────────
function parseMrzLines(lines) {
  // Normalizar: solo mayúsculas, dígitos y <
  const clean = lines
    .map(l => l.toUpperCase().replace(/[^A-Z0-9<]/g, ''))
    .filter(l => l.length >= 25);

  console.log('[MRZ] Líneas limpias:', clean);

  // Línea con nombre: contiene "<<" (separador apellido/nombre)
  const nameLine = clean.find(l => l.includes('<<'));
  let apellido = '', nombre = '';
  if (nameLine) {
    const idx = nameLine.indexOf('<<');
    apellido = nameLine.slice(0, idx).replace(/</g, ' ').trim();
    nombre   = nameLine.slice(idx + 2).replace(/</g, ' ').trim();
  }

  // DNI: primer número de 7-8 dígitos que no sea parte de uno de 9+ dígitos
  const allText = clean.join(' ');
  const dniMatch = allText.match(/(?<!\d)0*(\d{7,8})(?!\d)/);
  const dniParte = dniMatch ? dniMatch[1] : '';

  // Fecha de nacimiento: primera secuencia YYMMDD plausible
  let fechaNacimiento = '';
  const dateMatch = allText.match(/(?<!\d)(\d{2})(0[1-9]|1[0-2])([0-2]\d|3[01])(?!\d)/);
  if (dateMatch) {
    const yy = parseInt(dateMatch[1]);
    const year = yy > 30 ? 1900 + yy : 2000 + yy;
    fechaNacimiento = `${year}-${dateMatch[2]}-${dateMatch[3]}`;
  }

  // Sexo: M, F o X aislado entre < o espacios
  const sexMatch = allText.match(/[<\s]([MFX])[<\s]/);
  const sexo = sexMatch ? sexMatch[1] : '';

  return { apellido, nombre, sexo, dni: dniParte, fechaNacimiento };
}

// ── Componente ────────────────────────────────────────────────────────────────
export default function QrDniReader({ onResult, onClose }) {
  const [mode, setMode]               = useState('qr'); // 'qr' | 'mrz'

  // QR state
  const [fase, setFase]               = useState('iniciando');
  const [detectedDni, setDetectedDni] = useState('');
  const [dots, setDots]               = useState('');
  const [hasTorch, setHasTorch]       = useState(false);
  const [torchOn, setTorchOn]         = useState(false);

  // MRZ state
  const [mrzProcessing, setMrzProcessing] = useState(false);
  const [mrzPreview, setMrzPreview]       = useState(null);
  const [mrzStep, setMrzStep]             = useState('idle'); // 'idle' | 'processing' | 'done' | 'error'

  const [error, setError] = useState(null);

  const videoTrackRef  = useRef(null);
  const uniqueIdRef    = useRef(`qr-dni-reader-${Math.random().toString(36).substr(2, 9)}`);
  const qrRef          = useRef();
  const html5QrCodeRef = useRef(null);
  const frameCountRef  = useRef(0);
  const fileInputRef   = useRef(null);

  const [scanBox, setScanBox] = useState({ size: 350 });

  // Responsive
  useEffect(() => {
    function handleResize() {
      const w = window.innerWidth;
      if (w < 400) setScanBox({ size: w - 32 });
      else if (w < 600) setScanBox({ size: 320 });
      else setScanBox({ size: 350 });
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Puntos animados mientras escanea QR
  useEffect(() => {
    if (fase !== 'escaneando') return;
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(id);
  }, [fase]);

  async function toggleTorch() {
    const track = videoTrackRef.current;
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(t => !t);
    } catch (e) { console.warn('[QrDniReader] Linterna:', e); }
  }

  // ── QR scanner ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'qr') return;
    let isUnmounted = false;
    setHasTorch(false);
    setTorchOn(false);
    setError(null);
    setFase('iniciando');
    setDetectedDni('');
    videoTrackRef.current = null;
    frameCountRef.current = 0;

    const cleanup = () => {
      if (html5QrCodeRef.current) {
        try {
          if (typeof html5QrCodeRef.current.getState === 'function' && html5QrCodeRef.current.getState() === 2) {
            const r = html5QrCodeRef.current.stop(); if (r?.catch) r.catch(() => {});
          }
          if (typeof html5QrCodeRef.current.clear === 'function') {
            const r = html5QrCodeRef.current.clear(); if (r?.catch) r.catch(() => {});
          }
        } catch (e) {}
        html5QrCodeRef.current = null;
      }
      if (qrRef.current && qrRef.current.parentNode) qrRef.current.parentNode.removeChild(qrRef.current);
    };

    cleanup();

    const container = document.createElement('div');
    container.id = uniqueIdRef.current;
    container.style.width = `${scanBox.size}px`;
    container.style.height = `${scanBox.size}px`;
    qrRef.current = container;
    document.getElementById('qr-dni-parent')?.appendChild(container);

    const html5QrCode = new Html5Qrcode(uniqueIdRef.current, {
      formatsToSupport: [Html5QrcodeSupportedFormats.PDF_417, Html5QrcodeSupportedFormats.QR_CODE],
      verbose: false,
    });
    html5QrCodeRef.current = html5QrCode;

    console.log('[QrDniReader] Iniciando cámara...');

    html5QrCode
      .start(
        { facingMode: 'environment' },
        { fps: 15, aspectRatio: 1.0 },
        (decodedText) => {
          if (!decodedText || isUnmounted) return;
          console.log('[QrDniReader] ✅ CÓDIGO:', decodedText);
          const parsed = parseRenaperDni(decodedText);
          console.log('[QrDniReader] Parseado:', parsed);
          if (/^\d{7,8}$/.test(parsed.dni)) {
            try {
              if (typeof html5QrCode.getState === 'function' && html5QrCode.getState() === 2) {
                const r = html5QrCode.stop(); if (r?.catch) r.catch(() => {});
              }
            } catch (e) {}
            setFase('detectado');
            setDetectedDni(parsed.dni);
            setTimeout(() => { if (!isUnmounted) onResult(parsed); }, 600);
          } else {
            console.warn('[QrDniReader] Sin DNI válido:', parsed);
            setError('Código leído pero no se reconoce como DNI argentino. Apuntá al código del dorso.');
          }
        },
        () => {
          frameCountRef.current += 1;
          if (frameCountRef.current % 60 === 0) console.log(`[QrDniReader] Frame ${frameCountRef.current}`);
        }
      )
      .then(() => {
        if (isUnmounted) return;
        console.log('[QrDniReader] ✅ Cámara iniciada');
        setFase('escaneando');
        setTimeout(() => {
          if (isUnmounted) return;
          document.querySelectorAll('video').forEach(video => {
            const tracks = video.srcObject?.getVideoTracks?.();
            if (tracks?.length > 0 && tracks[0].getCapabilities?.()?.torch) {
              setHasTorch(true);
              videoTrackRef.current = tracks[0];
            }
          });
        }, 1200);
      })
      .catch(err => {
        console.error('[QrDniReader] ❌ Cámara:', err);
        if (!isUnmounted) { setFase('error'); setError('No se pudo iniciar la cámara. Verificá el permiso.'); }
      });

    return () => { isUnmounted = true; cleanup(); };
  }, [onResult, mode, scanBox.size]);

  // ── MRZ: procesamiento de foto ───────────────────────────────────────────────
  async function handleMrzPhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    // Resetear input para permitir volver a seleccionar el mismo archivo
    e.target.value = '';

    setError(null);
    setMrzStep('processing');
    setMrzPreview(URL.createObjectURL(file));

    try {
      // Cargar imagen en canvas
      const bitmap = await createImageBitmap(file);
      const { width: iw, height: ih } = bitmap;

      // Escalar a un máximo razonable para OCR
      const scale = Math.min(2, 1600 / Math.max(iw, ih));
      const sw = Math.round(iw * scale), sh = Math.round(ih * scale);
      const canvas = document.createElement('canvas');
      canvas.width = sw; canvas.height = sh;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, sw, sh);

      // Binarizar (blanco/negro) para mejor OCR
      const imgData = ctx.getImageData(0, 0, sw, sh);
      for (let i = 0; i < imgData.data.length; i += 4) {
        const avg = (imgData.data[i] + imgData.data[i+1] + imgData.data[i+2]) / 3;
        const v = avg > 140 ? 255 : 0;
        imgData.data[i] = imgData.data[i+1] = imgData.data[i+2] = v;
      }
      ctx.putImageData(imgData, 0, 0);

      console.log('[MRZ] Ejecutando OCR...');
      const { data: { text } } = await Tesseract.recognize(canvas, 'eng', {
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<',
      });
      console.log('[MRZ] OCR bruto:', text);

      const lines = text.split(/\n+/).map(l => l.trim()).filter(l => l.length >= 20);
      const parsed = parseMrzLines(lines);
      console.log('[MRZ] Parseado:', parsed);

      if (!/^\d{7,8}$/.test(parsed.dni)) {
        setError('No se pudo leer el DNI de la MRZ. Intentá con mejor iluminación o enfocando más la banda.');
        setMrzStep('error');
        return;
      }

      setMrzStep('done');
      setTimeout(() => onResult(parsed), 600);

    } catch (err) {
      console.error('[MRZ] Error:', err);
      setError('Error al procesar la foto: ' + (err.message || err));
      setMrzStep('error');
    }
  }

  // ── Cerrar ───────────────────────────────────────────────────────────────────
  const handleClose = () => {
    if (html5QrCodeRef.current) {
      try {
        if (typeof html5QrCodeRef.current.getState === 'function' && html5QrCodeRef.current.getState() === 2) {
          const r = html5QrCodeRef.current.stop(); if (r?.catch) r.catch(() => {});
        }
        if (typeof html5QrCodeRef.current.clear === 'function') {
          const r = html5QrCodeRef.current.clear(); if (r?.catch) r.catch(() => {});
        }
      } catch (e) {}
      html5QrCodeRef.current = null;
    }
    if (qrRef.current) qrRef.current.innerHTML = '';
    onClose();
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
      <h5 style={{ fontSize: 18, marginBottom: 10 }}>Escanear DNI</h5>

      {/* Toggle QR / MRZ */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <button
          type="button"
          onClick={() => { setMode(m => m === 'qr' ? 'mrz' : 'qr'); setError(null); setMrzStep('idle'); setMrzPreview(null); }}
          style={{
            background: mode === 'qr' ? '#1976d2' : '#7b1fa2',
            color: '#fff', border: 'none', borderRadius: 6,
            padding: '6px 14px', fontSize: 14, cursor: 'pointer',
          }}
        >
          {mode === 'qr' ? '🔤 Usar MRZ (foto)' : '📷 Usar código de barras'}
        </button>

        {mode === 'qr' && hasTorch && (
          <button
            type="button"
            onClick={toggleTorch}
            style={{
              background: torchOn ? '#ffc107' : '#555', color: torchOn ? '#333' : '#fff',
              border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 14,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <i className={`pi pi-${torchOn ? 'sun' : 'moon'}`} />
            {torchOn ? 'Linterna ON' : 'Linterna'}
          </button>
        )}
      </div>

      {/* ── Modo QR / PDF417 ── */}
      {mode === 'qr' && (
        <>
          <div
            id="qr-dni-parent"
            style={{
              position: 'relative', width: scanBox.size, height: scanBox.size,
              margin: '0 auto', maxWidth: '100%', borderRadius: 12, overflow: 'hidden',
              border: fase === 'detectado' ? '3px solid #43a047' : '3px solid #00e676',
              boxShadow: fase === 'detectado' ? '0 0 0 4px rgba(67,160,71,0.2)' : '0 0 0 4px rgba(0,230,118,0.15)',
              transition: 'border-color 0.3s',
            }}
          />

          {fase === 'iniciando' && (
            <div style={{ marginTop: 12, color: '#888', fontSize: 14 }}>Iniciando cámara...</div>
          )}
          {fase === 'escaneando' && (
            <div style={{ marginTop: 12, padding: '10px 16px', background: '#e3f2fd', borderRadius: 8, color: '#1976d2', fontSize: 15, fontWeight: 600 }}>
              🔍 Intentando leer{dots}
              <div style={{ fontSize: 12, fontWeight: 400, marginTop: 4, color: '#555' }}>
                Apuntá al <b>código del dorso</b>. Acercá y alejá para enfocar.
              </div>
            </div>
          )}
          {fase === 'detectado' && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: '#e8f5e9', borderRadius: 8, color: '#2e7d32', fontSize: 16, fontWeight: 700 }}>
              ✅ DNI {detectedDni} leído
              <div style={{ fontSize: 13, fontWeight: 400, marginTop: 4 }}>Consultando base de datos...</div>
            </div>
          )}
        </>
      )}

      {/* ── Modo MRZ (foto) ── */}
      {mode === 'mrz' && (
        <div>
          {/* Input oculto */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleMrzPhoto}
          />

          {mrzStep === 'idle' && (
            <>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 12, lineHeight: 1.5 }}>
                Fotografiá el <b>dorso</b> del DNI encuadrando toda la zona de texto inferior (la banda con las letras y signos &lt;).
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                style={{
                  background: '#7b1fa2', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '12px 24px', fontSize: 16, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600,
                }}
              >
                <i className="pi pi-camera" /> Tomar foto del DNI
              </button>
            </>
          )}

          {mrzStep === 'processing' && (
            <div style={{ padding: '20px 0' }}>
              {mrzPreview && (
                <img src={mrzPreview} alt="Vista previa" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 200, objectFit: 'contain' }} />
              )}
              <div style={{ color: '#7b1fa2', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <i className="pi pi-spin pi-spinner" />
                Procesando MRZ...
              </div>
            </div>
          )}

          {(mrzStep === 'error' || mrzStep === 'done') && (
            <>
              {mrzPreview && (
                <img src={mrzPreview} alt="Vista previa" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 10, maxHeight: 160, objectFit: 'contain' }} />
              )}
              {mrzStep === 'done' && (
                <div style={{ color: '#2e7d32', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>✅ MRZ leída correctamente</div>
              )}
              <button
                type="button"
                onClick={() => { setMrzStep('idle'); setMrzPreview(null); setError(null); }}
                style={{ background: '#7b1fa2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 15, cursor: 'pointer' }}
              >
                Intentar de nuevo
              </button>
            </>
          )}
        </div>
      )}

      {error && (
        <div style={{ color: '#c62828', background: '#ffebee', borderRadius: 8, padding: '10px 14px', marginTop: 10, fontSize: 14, fontWeight: 500 }}>
          {error}
        </div>
      )}

      <button onClick={handleClose} style={{ marginTop: 14, width: '100%', maxWidth: 320, fontSize: 16, padding: 10, borderRadius: 8 }}>
        Cerrar
      </button>
    </div>
  );
}
